import React, {useMemo} from 'react'
import {Box, Flex, Text, AspectRatio, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import Slider from '../../components/cms/slider'
import ModuleContainer from '../../components/cms/module-container'
import Asset from '../../components/cms/asset'
import gallerySlotRecipe from './index.recipe'
import Heading from '../../components/cms/heading'
import Copy from '../../components/cms/copy'

function Gallery({alignment, headingLevel, heading, copy, galleryItems, anchor, background}) {
    const videoRefs = React.useRef([])

    const recipe = useSlotRecipe({recipe: gallerySlotRecipe})
    const styles = useMemo(() => recipe({alignment}), [recipe, alignment])

    const getVideoRef = React.useCallback((i) => {
        if (!videoRefs.current[i]) {
            videoRefs.current[i] = React.createRef()
        }
        return videoRefs.current[i]
    }, [])

    return (
        <ModuleContainer
            anchor={anchor}
            background={background}
            {...styles.moduleContainer}
            heading={heading}
        >
            <Flex gap="spacing-16" alignment={alignment} flexDirection="column">
                <Heading {...heading} headingLevel={headingLevel} alignment={alignment}></Heading>
                <Copy {...copy} />
            </Flex>

            <Box pt="spacing-40">
                <Slider itemsPerSlide={1} variant="gallery">
                    {galleryItems.map((item, index) => {
                        const slideStyles = recipe({
                            darken: item.darken.replace('%', '')
                        })
                        return (
                            <Flex
                                key={index}
                                data-is-video={item.asset.type !== 'image'}
                                flexDirection="column"
                            >
                                <Box {...styles.textContainer}>
                                    {item.headline && (
                                        <Text
                                            color={{base: 'text.primary', s: 'text.on-surface'}}
                                            pb="spacing-8"
                                        >
                                            {item.headline}
                                        </Text>
                                    )}
                                    {item.copy?.text && (
                                        <Text
                                            color={{base: 'text.secondary', s: 'text.on-surface'}}
                                        >
                                            {item.copy.text}
                                        </Text>
                                    )}
                                </Box>

                                <Box position="relative">
                                    {/*top layer*/}
                                    {item.headline && <Box {...slideStyles.topLayer}></Box>}
                                    {/*bottom layer*/}
                                    <Box {...styles.bottomLayer}></Box>
                                    <AspectRatio ratio={{base: 1, s: 2}}>
                                        <Asset
                                            asset={item.asset}
                                            videoRef={getVideoRef(index)}
                                            objectFit="cover"
                                            widths={{base: '100vw', s: '87.5vw', l: 1680}}
                                            aspectRatio={{base: 1, s: 2}}
                                            variant="gallery"
                                        />
                                    </AspectRatio>
                                </Box>
                            </Flex>
                        )
                    })}
                </Slider>
            </Box>
        </ModuleContainer>
    )
}

Gallery.propTypes = {
    alignment: PropTypes.string,
    headingLevel: PropTypes.number,
    heading: PropTypes.string,
    copy: PropTypes.object,
    galleryItems: PropTypes.array,
    anchor: PropTypes.string,
    background: PropTypes.string
}

export default Gallery
